import { useVirtualizer } from "@tanstack/react-virtual";
import { useMap } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useRef } from "react";

import useCaseMapStore from "stores/useCaseMapStore";
import { flyTo } from "./helpers";
import { CaseMapSidebarRow } from "./CaseMapSidebarRow";
import { CaseMapSidebarSkeleton } from "./CaseMapSidebarRowSkeleton";
import { EmptyResults } from "./EmptyResult";

// A row is three or four short lines; the virtualizer measures the real height
// once each row mounts.
const ESTIMATED_ROW_SIZE = 108;

// Past supercluster's maxZoom of 14, so the machine is guaranteed to be its own
// pin rather than swallowed by a cluster bubble.
const SELECTION_ZOOM = 15;

export function CaseMapSidebar({
  machines,
  positionsById,
  isLoading,
  hasFilters,
  canEditStatus,
}) {
  const scrollRef = useRef(null);
  const map = useMap();
  const selectedMachineId = useCaseMapStore(s => s.selectedMachineId);
  const selectMachine = useCaseMapStore(s => s.selectMachine);
  const checkedMachineIds = useCaseMapStore(s => s.checkedMachineIds);
  const toggleChecked = useCaseMapStore(s => s.toggleChecked);

  const virtualizer = useVirtualizer({
    count: machines.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_SIZE,
    overscan: 10,
  });

  const selectedIndex = useMemo(
    () => machines.findIndex(machine => machine.id === selectedMachineId),
    [machines, selectedMachineId]
  );

  const handleSelect = useCallback(
    machineId => {
      selectMachine(machineId);

      const position = positionsById.get(machineId);
      if (map && position) {
        flyTo(map, {
          ...position,
          zoom: Math.max(map.getZoom(), SELECTION_ZOOM),
        });
      }
    },
    [map, positionsById, selectMachine]
  );

  // Keeps the map→sidebar half of the sync honest: picking a pin has to bring
  // its row into view, and virtualized rows may not be mounted at all.
  useEffect(() => {
    if (selectedIndex >= 0) {
      virtualizer.scrollToIndex(selectedIndex, {
        align: "center",
        behavior: "smooth",
      });
    }
  }, [selectedIndex, virtualizer]);

  const isEmpty = !machines.length;

  return (
    <aside
      className="tw:flex tw:w-80 tw:shrink-0 tw:flex-col tw:overflow-y-auto tw:border-r tw:border-(--color-border) tw:bg-(--color-surface)"
      ref={scrollRef}
    >
      {isLoading && isEmpty ? (
        <CaseMapSidebarSkeleton selectable={canEditStatus} />
      ) : isEmpty ? (
        <EmptyResults hasFilters={hasFilters} />
      ) : (
        <div
          className="tw:relative tw:w-full"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualizer.getVirtualItems().map(virtualRow => {
            const machine = machines[virtualRow.index];

            return (
              <div
                key={machine.id}
                ref={virtualizer.measureElement}
                data-index={virtualRow.index}
                className="tw:absolute tw:top-0 tw:left-0 tw:w-full"
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                <CaseMapSidebarRow
                  machine={machine}
                  selected={machine.id === selectedMachineId}
                  notOnMap={!positionsById.has(machine.id)}
                  onSelect={handleSelect}
                  selectable={canEditStatus}
                  checked={checkedMachineIds.includes(machine.id)}
                  onToggleChecked={toggleChecked}
                />
              </div>
            );
          })}
        </div>
      )}
    </aside>
  );
}
